/**
 * @file JS - Helpers - Selectors.js
 * @author : Adam Lemire
 * @since 2019/06/20
 * @version 1.0
 * @summary Dernières modifications : 2021/06/16
 */

/**
 * @description Boutons du SIG (sélecteurs)
 * @enum {string}
 */
export const GisButton = {
  GIS_PROJECT_MENU: '.top-menu__project-menu-button',
  PROJECT_SAVE: '.top-menu__save-button',
  PROJECT_SAVE_API: '.top-menu__save-api-button',
  PROJECT_ADD_SINGLE_MAP: '.top-menu__add-map-page-button',
  MAP_IMPORT_DATA: '.top-menu__import-button',
  MAP_IMPORT_DATA_API: '.top-menu__import-data-api-button',
  MAP_ZOOM_OUT: '.top-menu__zoom-out-button',
  MAP_CENTER_MAP: '.top-menu__center-page-button',
  MAP_ZOOM_IN: '.top-menu__zoom-in-button',
  MAP_EXPORT_AS_IMAGE: '.top-menu__export-map-as-image',
  GIS_HELP: '.top-menu__help-button',
  GIS_LANGUAGE: '.top-menu__language-button',
  GIS_MAP_LAYER: '.map-layers-button',
  GIS_WEB_LAYER: '.web-layers-button',
  GIS_MULTI_MAPS: '.multi-maps-button',
  GIS_PRINT_LAYOUT: '.print-layout-button',
  GIS_LEFT_MENU: '.expand-menu-button',
  GIS_LEFT_MENU_BUTTONS: '.left-menu__button'
};

/**
 * @description Sections du SIG (sélecteurs)
 * @enum {string}
 */
export const GisSection = {
  HEADER: {
    SECTION: '.project-title',
    PROJECT_NAME: '.project-title__name',
    MAP_NAME: '.project-title__map-name'
  },
  LEFT_MENU: '.left-menu',
  LEFT_MENU_DETAILS: '.left-menu__details',
  LEFT_MENU_DETAIL: '.menu-details__tab',
  MAP_LAYERS: '.map-layers',
  MAP_LAYERS_CONTAINER: '#map-layers__container',
  MAP_LAYERS_LAYER: '.map-layers__layer',
  WEB_LAYERS: '.web-layers',
  MULTI_MAP_DETAILS: '.multimap',
  PRINT_LAYOUT_OPTIONS: '.print-layout-options',
  CONTENT: '.content',
  DATA: '.data',
  MAP: '.map',
  DATA_TABS: '.data-tabs',
  MAP_TABS: {
    SECTION: '.map-tabs',
    TAB: '.map-tabs__tab'
  }
};

/**
 * @description Menu contextuels du SIG (sélecteurs)
 * @enum {string}
 */
export const ContextMenu = {
  ALL: '.context-menu',
  HEADER: {
    SECTION: '.header-context-menu',
    RENAME: '.header-options__rename'
  },
  MAP_LAYERS: '.map-layer-context-menu',
  MAP_TABS: '.map-tabs-context-menu',
  PROJECT_MENU: '.project-menu'
};

/**
 * @description contenur du menu contextuel du projet
 * @enum {string}
 */
export const ProjectMenu = {
  BUTTONS: '.project-menu__button',
  BOUTON_EXPORTER: '.project-menu__export-button',
  BOUTON_NOUVEAU: '.project-menu__new-project-button',
  OPEN_BUTTON: '.project-menu__open-project-button',
  OPEN_PROJECT_FORM: {
    SECTION: '.open-project-form',
    INPUT: '.open-project-form__file-input',
    OPEN: '.open-project-form__open-button',
    CANCEL: '.open-project-form__cancel-button',
    NO_PROJECT: '.open-project-form__aucun-projet',
    NO_PROJECT_TEXT: '.open-project-form__aucun-projet-texte',
    FILE_NAME: '.open-project-form__file-name'
  },
  OUVRIR_PROJET_CHERCHEUR_BUTTON: '.project-menu__ouvrir-projet-chercheur-button',
  RENAME_BUTTON: '.project-menu__rename-project-button',
  RENAME_FORM: {
    SECTION: '.project-rename-form',
    INPUT: '.project-rename-form__input',
    APPLY: '.project-rename-form__apply-button',
    CANCEL: '.project-rename-form__cancel-button'
  },
  IMPORT_DATA_BUTTON: '.project-menu__import-data-button',
  IMPORT_REASERCHER_DATA_BUTTON: '.project-menu__import-researcher-data-button'
};

/**
 * @description Options du menu contextuel des onglets des cartes (MAP_TABS)
 * @enum {string}
 */
export const MapTabsContextMenuOptions = {
  RENAME: '.map-tabs-options__rename',
  DUPLICATE: '.map-tabs-options__duplicate',
  DELETE: '.map-tabs-options__delete'
};

/**
 * @description Formulaire pour renommer une carte
 * @enum {string}
 */
export const MapRenameForm = {
  FORM: '.map-rename-form',
  INPUT: '.map-rename-form__input',
  APPLY: '.map-rename-form__apply-button',
  CANCEL: '.map-rename-form__cancel-button'
};

/**
 * @description Options du menu contextuel des couches d'une carte (MAP_LAYERS)
 * @enum {string}
 */
export const LayerContextMenuOptions = {
  PROPERTIES: '.map-layer-options__properties',
  COPY: '.map-layer-options__copy',
  PASTE: '.map-layer-options__paste',
  ATTRIBUTES_TABLE: '.map-layer-options__attributes-table',
  REMOVE: '.map-layer-options__remove'
};

/**
 * @description Formulaire des propriétés d'une couche (layer)
 * @enum {string}
 */
export const LayerProperties = {
  ADVANCED_PREVIEW: {
    CONTAINER: '.layer-style__advanced-preview',
    ROW: '.advanced-preview__row'
  },
  BUTTON: {
    APPLY: '.layer-properties__apply-button',
    CANCEL: '.layer-properties__cancel-button',
    STROKE_COLOR: '.layer-style__stroke-color'
  },
  COLOR_PICKER: {
    CONTENT: '.color-picker__content',
    CONTAINER: '.color-picker',
    APPLY_BUTTON: '.color-picker__apply-button',
    CANCEL_BUTTON: '.color-picker__cancel-button'
  },
  FILL: {
    COLOR: '.symbol-style__fill-color',
    COLOR_LABEL: '.symbol-style__fill-color-label',
    TITLE_LABEL: '.symbol-style__fill-title'
  },
  SECTION: '.layer-properties',
  SECTION_RASTER: '.layer-properties__raster',
  SECTION_VECTOR: '.layer-properties__vector',
  HEADER: '.layer-properties__header',
  SOURCE_NAME: '.layer-source__value',
  LAYER_STYLE: '.layer-style',
  LABEL_SYMBOL_SIZE: '.layer-style__symbol-size-label',
  LAYER_NAME: '.layer-name__value',
  LAYER_STROKE_WIDTH: '.layer-style__stroke-width',
  LAYER_SYMBOL_SIZE: '.layer-style__symbol-size',
  LAYER_SYMBOL_PREVIEW: '.layer-style__symbol-preview',
  SYMBOL_STYLE: '.symbol-style',
  SYMBOL_STYLE_TYPE: '.layer-style__type',
  SYMBOL_STYLE_GROUP_LABEL: '.symbol-style__group-label',
  GROUP_CLASSIFICATION_SELECTOR: '.layer-style__group-classification-selector',
  GROUP_CLASSIFICATION_QUANTITY_SELECTOR: '.layer-style__group-quantity-selector',
  GROUP_CLASSIFICATION_QUANTITY_ROW: '.layer-style__group-quantity',
  CLASSIFICATION_TYPE_GEOMETRIC: '.group-classification__geometric-progression',
  GROUP_VALUE_SELECTOR: '.layer-style__group-value-selector',
  POINT_GRADATION_TYPE: '.layer-style__gradation-type',
  POINT_GRADATION_TYPE_INPUT: '.gradation-type input',
  GRADATION_TYPE: '.layer-style__gradation-type',
  GRADATION_TYPE_INPUT: '.gradation-type__input',
  GRADATION_TYPE_SIZE: '.gradation-type-size',
  PRECISION: '.layer-style__precision',
  PRECISION_SELECTOR: '.layer-style__precision-selector',
  COLOR_GRADIENT_SELECTOR: '.layer-style__color-gradient-selector',
  COLOR_THEME_SELECTOR: '.layer-style__color-theme-selector',
  OPACITY_SLIDER: '.layer-style__opacity-slider',
  OPACITY_VALUE: '.layer-style__opacity-value',
  INFO_CLASSIFICATION_BOUTON: '.info-classification-button',
  INFO_CLASSIFICATION_FERMER: '.info-classification__close-button',
  INFO_NULLES: '.layer-style__nulles',
  LAYER_SOURCE_AUTRES: '.layer-source-autres',
  LAYER_SOURCE_AUTRES_CHECKBOX: '.layer-source-autres__value',
  LAYER_SOURCE_POINT: '.layer-source-point',
  LAYER_SOURCE_POINT_CHECKBOX: '.layer-source-point__value',
  LAYER_SOURCE_VECTOR: '.layer-source__vector',
  SYMBOLE_AUTRES: '.symbol-style-autres',
  SYMBOLE_AUTRES_COULEUR: '.symbol-style-autres__fill-color',
  SYMBOLE_AUTRES_CONTOUR_COULEUR: '.symbol-style-autres__stroke-color',
  SYMBOLE_AUTRES_CONTOUR_EPAISSEUR: '.symbol-style-autres__stroke-width',
  SYMBOLE_AUTRES_TAILLE: '.symbol-style-autres__symbol-size',
  SYMBOLE_AUTRES_TAILLE_ETIQUETTE: '.symbol-style-autres__symbol-size-label',
  SYMBOLE_AUTRES_APERCU: '.symbol-style-autres__symbol-preview'
};

export const FormulaireOuvrirProjetChercheur = {
  SECTION: '.ouvrir-projet-chercheur-form',
  LISTE_PROJETS: '.ouvrir-projet-chercheur-form__liste-projets',
  TEXTE_AUCUN_PROJET: '.ouvrir-projet-chercheur-form__aucun-projet',
  OPEN_BUTTON: '.ouvrir-projet-chercheur-form__open-button',
  CANCEL_BUTTON: '.ouvrir-projet-chercheur-form__cancel-button'
};

export const ImportDataForm = {
  SECTION: '.import-data-form',
  FILE_INPUT: '.import-data-form__file-input',
  FILE_NAME: '.file-preview__file-name',
  FILE_PREVIEW: '.import-data-form__file-preview',
  NO_FILE_TEXT: '.file-preview__no-file',
  CANCEL_BUTTON: '.import-data-form__cancel-button',
  IMPORT_BUTTON: '.import-data-form__import-button'
};

export const ImportResearcherDataForm = {
  SECTION: '.import-researcher-data-form',
  FILE_LIST: '.import-researcher-data-form__file-list',
  NO_FILE_TEXT: '.import-researcher-data-form__no-file',
  CANCEL_BUTTON: '.import-researcher-data-form__cancel-button',
  IMPORT_BUTTON: '.import-researcher-data-form__import-button'
};

export const LayoutSection = {
  CONTENEUR: '.export-layout__preview-container'
};

export default class Selectors {
}
